import { EligibilityVariant } from "./getEligibilityView";

/**
 * Class names of the legal protection first step, gathered here so that no component copies them.
 * The main colour is a platform theme: it always comes from `primary` (`--primary-color`) and is
 * never written as an hexadecimal. The neutral and status colours below are fixed by the design.
 */

/** Text colours of the step. */
export const LP_TEXT = {
  /** Company and subsidiary names, figures. */
  title: "text-[#1f2a2b]",
  /** Running text. */
  body: "text-content",
  /** Section labels, micro-labels and secondary lines. */
  secondary: "text-[#5f6f71]",
  /** Greyed out section: label, field and value. */
  disabled: "text-[#9fb3b4]",
} as const;

/** Border colours of the step. */
export const LP_BORDER = {
  /** Outline of a filled card. */
  card: "border-[#e2e8e8]",
  /** Rule inside a card, between the identity and the figures. */
  rule: "border-[#eef2f2]",
  /** Dashed outline of an empty card. */
  empty: "border-[#cfdcdc]",
  /** Dashed outline of an empty card inside a greyed out section. */
  emptyDisabled: "border-[#e2e8e8]",
} as const;

/** Background colours of the step, outside the status banner. */
export const LP_SURFACE = {
  /** Disabled field or reminder of a greyed out section. */
  disabled: "bg-[#f8fafa]",
  /** Reminder of the cumulated figures, below the accounting fields. */
  totals: "bg-[#f6f9f9]",
} as const;

/** Sizes of the step: the bold weight is kept for names and figures. */
export const LP_TYPO = {
  /** Name of the company. */
  companyName: "text-[20px] font-bold",
  /** Name of a subsidiary. */
  subsidiaryName: "text-[17px] font-bold",
  /** Label above a figure, in capitals. */
  microLabel: "text-[11px] font-bold uppercase tracking-[0.08em]",
  /** Figure inside a card. */
  cardFigure: "text-[17px] font-bold tabular-nums",
} as const;

/** Verdict of the back, plus the neutral state where there is no verdict yet. */
export type LegalProtectionStatus = Exclude<EligibilityVariant, "notApplicable"> | "neutral";

export interface LegalProtectionStatusTheme {
  /** Background of the banner. */
  background: string;
  /** Outline of the banner and of its inner rules. */
  border: string;
  /** Status title and figures. */
  text: string;
  /** Reasons and micro-labels of the banner, one step softer than the title. */
  softText: string;
}

export const LP_STATUS: Record<LegalProtectionStatus, LegalProtectionStatusTheme> = {
  eligible: {
    background: "bg-[#eaf6ee]",
    border: "border-[#b8ddc4]",
    text: "text-[#14532d]",
    softText: "text-[#1c6b3a]",
  },
  toComplete: {
    background: "bg-[#fdf3e3]",
    border: "border-[#f0d9ab]",
    text: "text-[#7a4e05]",
    softText: "text-[#7a4e05]",
  },
  nonEligible: {
    background: "bg-[#fdecec]",
    border: "border-[#f0c3c1]",
    text: "text-[#7f1d1a]",
    softText: "text-[#8f2320]",
  },
  neutral: {
    background: "bg-[#f6f9f9]",
    border: "border-[#e2e8e8]",
    text: "text-[#5f6f71]",
    softText: "text-[#5f6f71]",
  },
};
